import { useMutation, useQuery } from '@tanstack/react-query';
import {
  type NoticeAction,
  type NoticeboardFilter,
  type QueryAdditionalOptions,
  type RetrySocialPostInput,
  type UseQueryReturnType,
  gqlClient,
  graphql,
  queryClient,
} from '@tyro/api';
import { noticeBoardKeys } from './keys';

const noticeBoard = graphql(/* GraphQL */ `
  query communications_noticeboard($filter: NoticeboardFilter) {
    communications_noticeboard(filter: $filter) {
      id
      title
      notice
      priority
      validFrom
      validUntil
      images {
        id
        url
        isCover
      }
      recipients {
        id {
          noticeId
          partyId
        }
        acknowledged
        acknowledgedOn
      }
      pinned
      publicNotice
      socialPosts {
        id
        socialPostId
        platform
        status
        retryCount
        maxRetries
        errorMessage
        nextRetryAt
      }
    }
  }
`);

const noticeBoardDetails = graphql(/* GraphQL */ `
  query communications_noticeboardDetails($filter: NoticeboardFilter) {
    communications_noticeboard(filter: $filter) {
      id
      title
      notice
      priority
      validFrom
      validUntil
      images {
        id
        url
        isCover
      }
      allStaff
      allContacts
      allStudents
      assignedToParties {
        id {
          noticeId
          partyId
        },
        staff
        contact
        student
        party {
          __typename
          ... on GeneralGroup {
            name
            partyId
            avatarUrl
          }
          ... on ProgrammeStageEnrollment {
            partyId
            name
          }
          ... on SubjectGroup {
            name
            partyId
            avatarUrl
          }
          ... on YearGroupEnrollment {
            name
            partyId
          }
          ... on Staff {
            partyId
            person {
              partyId
              firstName
              lastName
              avatarUrl
              type
              title {
                id
                name
                nameTextId
              }
            }
          }
          ... on Student {
            partyId
            person {
              partyId
              firstName
              lastName
              avatarUrl
              type
              title {
                id
                name
                nameTextId
              }
            }
          }
          ... on StudentContact {
            partyId
            person {
              partyId
              firstName
              lastName
              avatarUrl
              type
              title {
                id
                name
                nameTextId
              }
            }
          }
        }
      }
      recipients {
        id {
          noticeId
          partyId
        }
        acknowledged
        acknowledgedOn
      }
      pinned
      archived
      publicNotice
      socialPosts {
        id
        socialPostId
        platform
        status
        retryCount
        maxRetries
        errorMessage
        nextRetryAt
      }
      archivedByPartyId
      archivedBy {
        partyId
        title {
          id
          name
        }
        firstName
        lastName
        avatarUrl
        type
        archived
      }
    }
  }
`);

const acknowledgeNotice = graphql(/* GraphQL */ `
  mutation communications_acknowledgeNotice($input: NoticeAction) {
    communications_acknowledgeNotice(input: $input) {
      success
    }
  }
`);

const retrySocialPost = graphql(/* GraphQL */ `
  mutation communications_retrySocialPost($input: RetrySocialPostInput!) {
    communications_retrySocialPost(input: $input) {
      success
    }
  }
`);

const noticeBoardQuery = (filter: NoticeboardFilter) => ({
  queryKey: noticeBoardKeys.noticeBoard(filter),
  queryFn: () =>
    gqlClient.request(noticeBoard, {
      filter,
    }),
});

export function getNoticeBoard(filter: NoticeboardFilter) {
  return queryClient.fetchQuery(noticeBoardQuery(filter));
}
export function useNoticeBoard(filter: NoticeboardFilter) {
  return useQuery({
    ...noticeBoardQuery(filter),
    select: ({ communications_noticeboard }) =>
      communications_noticeboard.sort(
        (a, b) => (b.priority ?? 0) - (a.priority ?? 0),
      ),
  });
}

const noticeBoardDetailsQuery = (filter: NoticeboardFilter) => ({
  queryKey: noticeBoardKeys.noticeBoardDetails(filter),
  queryFn: () =>
    gqlClient.request(noticeBoardDetails, {
      filter,
    }),
});

export function getNoticeBoardDetails(filter: NoticeboardFilter) {
  return queryClient.fetchQuery(noticeBoardDetailsQuery(filter));
}
export function useNoticeBoardDetails(
  filter: NoticeboardFilter,
  additionalOptions?: Omit<QueryAdditionalOptions, 'academicNamespaceId'>,
) {
  return useQuery({
    ...noticeBoardDetailsQuery(filter),
    ...additionalOptions,
    select: ({ communications_noticeboard }) =>
      communications_noticeboard.sort((a, b) => {
        return (b.priority ?? 0) - (a.priority ?? 0);
      }),
  });
}

export function useAcknowledgeNotice() {
  return useMutation({
    mutationFn: async (input: NoticeAction) =>
      gqlClient.request(acknowledgeNotice, { input }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: noticeBoardKeys.all });
    },
  });
}

export function useRetrySocialPost() {
  return useMutation({
    mutationFn: async (input: RetrySocialPostInput) =>
      gqlClient.request(retrySocialPost, { input }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: noticeBoardKeys.all });
    },
  });
}

export type ReturnTypeFromUseNoticeBoard = UseQueryReturnType<
  typeof useNoticeBoard
>[number];

export type ReturnTypeFromUseNoticeBoardDetails = UseQueryReturnType<
  typeof useNoticeBoardDetails
>[number];
